import React, { createContext, useContext, useState, ReactNode } from "react";
import Toast from "../components/Toast";
import { useQuery } from "react-query";
import * as apiClient from "../api-client";

type ToastMessage = {
  message: string;
  type: "SUCCESS" | "ERROR";
};

type UserType = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
};


interface AppContextType {
  showToast: (toastMessage: ToastMessage) => void;
  isLoggedIn: boolean;
  user: UserType | null;
  setUser: (user: UserType | null) => void;
  isLoading: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppContextProviderProps {
  children: ReactNode;
}

const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastMessage | undefined>(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<UserType | null>(null);

  // Add retry configuration and better error handling
  const { isLoading } = useQuery("validateToken", apiClient.validateToken, {
    onSuccess: (data) => {
      if (data) {
        setUser(data);
        setIsLoggedIn(true);
      }
    },
    onError: () => {
      setUser(null);
      setIsLoggedIn(false);
    },
    retry: 1,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
    refetchOnWindowFocus: false,
  });

  const updateUserAndLoginState = (newUser: UserType | null) => {
    setUser(newUser);
    setIsLoggedIn(!!newUser);
  };

  const value = {
    showToast: (toastMessage: ToastMessage) => setToast(toastMessage),
    isLoggedIn,
    user,
    setUser: updateUserAndLoginState,
    isLoading,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(undefined)} />}
    </AppContext.Provider>
  );
};

const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
};

export { AppContextProvider, useAppContext };